import jsPDF from "jspdf";

interface FontStyle {
  font?: string;
  fontSize?: number;
  color?: Color;
  style?: "normal" | "italic" | "bold" | "bolditalic";
}

interface Color {
  r: number;
  g: number;
  b: number;
}

type Alignment = "center" | "left" | "right";

const DEFAULT_FONT = "helvetica";

interface ImageOptions {
  resizeFactor?: number;
  format?: "png" | "bmp" | "jpeg";
}

export class PDFWriter {
  yPos: number;
  doc: jsPDF;
  width: number;

  columnStartYPos = 0;
  columnMaxHeight = 0;
  columnCount = 0;
  columnCurrent: { idx: number; span: number; xPos: number; width: number } | null = null;

  paragraphAfter = 5;
  margin = 5;
  innerWidth = 0;

  defaultFont: FontStyle = {
    font: DEFAULT_FONT,
    color: this.monoColor(0),
    fontSize: 10,
    style: "normal"
  };
  currentFont: FontStyle = {
    ...this.defaultFont
  };

  constructor() {
    this.yPos = this.margin;
    this.doc = new jsPDF({
      unit: "mm",
      format: "a4"
    });
    this.width = this.doc.internal.pageSize.getWidth();
    this.fontDefault();
    this.innerWidth = this.width - this.margin * 2;
  }

  fontDefault() {
    this.font(this.defaultFont);
  }

  monoColor(greyFactor: number): Color {
    return {
      r: greyFactor,
      g: greyFactor,
      b: greyFactor
    };
  }

  font(props: FontStyle) {
    if (props.fontSize) {
      this.currentFont.fontSize = props.fontSize;
      this.doc.setFontSize(props.fontSize);
    }
    if (props.color) {
      this.currentFont.color = props.color;
      this.doc.setTextColor(props.color.r || 0, props.color.g || 0, props.color.b || 0);
    }
    let changeFont = false;
    if (props.style) {
      changeFont = true;
      this.currentFont.style = props.style;
    }
    if (props.font) {
      changeFont = true;
      this.currentFont.font = props.font;
    }
    if (changeFont) {
      this.doc.setFont(props.font || DEFAULT_FONT, props.style, props.style === "bold" ? "bold" : undefined);
    }
  }

  text(content: string, align: Alignment, xPos: number, style?: FontStyle) {
    if (style) {
      this.font(style);
    }
    const dim = this.multiLineDim(content);
    this.doc.text(content, this.privCalcXPos(dim.w, align, xPos), this.yPos);
    this.yPos += dim.h + this.paragraphAfter;
  }

  image(imgContent: string, align: Alignment, xPos: number, options?: Partial<ImageOptions>) {
    const props = this.doc.getImageProperties(imgContent);
    const factor = options?.resizeFactor || 1;
    this.doc.addImage(
      imgContent,
      options?.format || "png",
      this.privCalcXPos(this.pxToMM(props.width * factor), align, xPos),
      this.yPos,
      this.pxToMM(props.width * factor),
      this.pxToMM(props.height * factor)
    );
    this.yPos += this.pxToMM(props.height * factor) + this.paragraphAfter;
  }

  columnsStart(count: number) {
    this.columnStartYPos = this.yPos;
    this.columnCount = count;
    this.columnMaxHeight = 0;
  }

  columnsEnd() {
    this.privCheckMaxColumnHeight();
    this.yPos = this.columnStartYPos + this.columnMaxHeight;
    this.columnStartYPos = 0;
    this.columnMaxHeight = 0;
    this.columnCount = 0;
    this.columnCurrent = null;
  }

  setColumn(idx: number, span?: number) {
    this.privCheckMaxColumnHeight();
    const colWidth = this.innerWidth / this.columnCount;
    this.columnCurrent = {
      idx,
      span: span || 1,
      width: colWidth * (span || 1),
      xPos: this.margin + idx * colWidth
    };
    this.yPos = this.columnStartYPos;
  }

  verticalPad(mm: number) {
    this.yPos += mm;
  }

  pxToMM(pixels: number): number {
    return pixels * 0.264583;
  }

  multiLineDim(content: string): { w: number; h: number } {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dim = this.doc.getTextDimensions(content.split("\n") as any); // Castea 'any' porque esta mal el tipo de typescript
    return dim;
  }

  hr() {
    const height = this.multiLineDim("A").h;
    // const extraHeight = (height * this.lineHeight) - height;
    this.verticalPad(-1 * (height / 2));
    this.doc.setLineWidth(0.1);
    this.doc.setDrawColor(200, 200, 200);
    this.doc.line(this.margin, this.yPos, this.width - this.margin, this.yPos);
    this.verticalPad(height / 2 + height + this.paragraphAfter);
  }

  save(fileName: string) {
    this.doc.save(fileName);
  }

  privCalcXPos(elemWidth: number, align: Alignment, xOffset: number): number {
    let width = this.innerWidth;
    let newOffset = this.margin + xOffset;
    if (this.columnCurrent) {
      width = this.columnCurrent.width;
      newOffset = this.columnCurrent.xPos + xOffset;
    }
    if (align === "center") {
      return width / 2 - elemWidth / 2 + newOffset;
    } else if (align === "right") {
      return width - elemWidth + newOffset;
    }
    return newOffset;
  }

  privCheckMaxColumnHeight() {
    if (this.yPos > this.columnStartYPos) {
      const prevColHeight = this.yPos - this.columnStartYPos;
      if (this.columnMaxHeight < prevColHeight) {
        this.columnMaxHeight = prevColHeight;
      }
    }
  }
}
